import React, { ComponentPropsWithoutRef } from 'react';

import classNames from 'classnames';

import ImageProxy from '@next-image-proxy';

import { Button, ExternalLink } from '@uikit';

import TeamMemberList from '@launchpad/components/common/TeamMemberList/TeamMemberList';

import { ItemsProps, ProjectContentTableOfContentTab } from '../types';
import { keyFeatures, marketTraction, teamMembers, tokenUtility } from './constants';

export interface ListProps extends ComponentPropsWithoutRef<'ul'> {
  items: Array<ItemsProps>;
  description?: string;
  descriptionClassName?: string;
  withNumericSymbols?: boolean;
}

const List = ({
  items,
  description,
  className,
  descriptionClassName,
  withNumericSymbols,
}: ListProps) => {
  return (
    <>
      {Boolean(description) && (
        <p
          className={classNames(
            'mb-4 sm:text-2xl font-semibold text-neutral-500',
            descriptionClassName,
          )}
        >
          {description}
        </p>
      )}
      <ul
        className={classNames(
          'pl-6',
          {
            'list-decimal': withNumericSymbols,
            'list-disc': !withNumericSymbols,
          },
          className,
        )}
      >
        {items.map(({ id, content, item }) => {
          if (Array.isArray(item)) {
            return (
              <li key={id} className="mb-4">
                {content}
                <ul className="list-disc pl-6">
                  {item.map(({ id: subId, subItem }) => (
                    <li key={subId}>{subItem}</li>
                  ))}
                </ul>
              </li>
            );
          } else {
            return (
              <li className="max-sm:text-sm" key={id}>
                {item}
              </li>
            );
          }
        })}
      </ul>
    </>
  );
};

export const tableOfContentTab: ProjectContentTableOfContentTab['content'] = [
  {
    id: 'how-to-participate',
    title: 'How to Participate',
    content: (
      <p className="mb-4 max-sm:text-sm font-normal">
        Participation in this IDO is open to all Verified-KYC Magic Launchpad members. No whitelist
        or staking is required. <b>This IDO includes a 24-hour refund policy.</b>
      </p>
    ),
  },
  {
    id: 'intro',
    title: 'Introduction',
    content: (
      <p className="mb-4 max-sm:text-sm font-normal">
        By integrating <b>EarnOS</b> and <b>EARN’M SmartWallet</b>, Earn’M seamlessly merges Web2
        and Web3. Through the <b>Mode Earn App</b> and the <b>EarnPhone</b>, Earn’M offers
        gas-subsidized, on-chain earning opportunities for mobile users globally. Its proprietary
        EarnOS allows device manufacturers, carriers, and app developers to easily reward users for
        digital activities, creating a groundbreaking model where smartphones become
        revenue-generating assets. This approach attracts both Web2 and Web3 clients, including
        partnerships with retail giants like Walmart, Target, and Amazon, plus Web3 leaders such as
        Binance and Coinbase.
      </p>
    ),
  },
  {
    id: 'key-features',
    title: 'Key Features',
    content: (
      <p className="mb-4">
        <List items={keyFeatures} />
      </p>
    ),
  },
  {
    id: 'problem-solution',
    title: 'Problem and Solution',
    content: (
      <ul className="mb-4 list-none pl-0 max-sm:text-sm font-normal">
        <li className="mb-3">
          <b>Problem:</b> Traditional Web2 environments limit global earning opportunities due to
          reward disparities and complex onboarding to Web3, where high fees and technical barriers
          prevent meaningful engagement in decentralized economies.
        </li>
        <li>
          <b>Solution:</b> Earn’M’s EarnPhone and SmartWallet empower users to interact seamlessly
          in Web3, allowing anyone to benefit from blockchain-driven rewards without technical
          complexity. EarnOS uses data and attention as currency, transforming user engagement into
          income through Web3.
        </li>
      </ul>
    ),
  },
  {
    id: 'market-traction-achievements',
    title: 'Market Traction / Achievements',
    content: (
      <>
        <List items={marketTraction} />
        <ImageProxy
          alt="Dune Analytics"
          src="/launchpad/stories/earnM/earnm-dune-analytics.png"
          className="mb-6 block w-full h-auto rounded-3xl object-contain"
          quality={80}
          width={800}
          height={285}
        />
        <ImageProxy
          alt="Earnm User Stats"
          src="/launchpad/stories/earnM/earnm-searnm-user-stats.png"
          className="block w-full h-auto rounded-3xl object-contain"
          quality={80}
          width={773}
          height={264}
        />
      </>
    ),
  },
  {
    id: 'investors-and-partners',
    title: 'Investors and Partners',
    content: (
      <p className="pt-4">
        <ImageProxy
          alt="Loyalty Rewards Flywheel"
          src="/launchpad/stories/earnM/earnm-investors-and-partners.png"
          className="block w-full h-auto rounded-3xl object-contain"
          quality={80}
          width={773}
          height={435}
        />
      </p>
    ),
  },
  {
    id: 'business-model',
    title: 'Business Model',
    content: (
      <ul className="list-none pl-0 max-sm:text-sm font-normal">
        <li className="mb-3">
          <p className="mb-3">
            <b>Sustainable Value Creation through Dual Models:</b>
          </p>
          <ul className="pl-6 list-disc max-sm:text-sm font-normal">
            <li>
              <b>B2C:</b> Users earn Fractal assets (Mystery Boxes) through digital engagement, with
              each transaction benefiting Earn’M via network fees.
            </li>
            <li>
              <b>B2B:</b> Partners can integrate <b>EarnOS</b> and <b>SmartWallet</b> using no-code,
              plug-and-play access, expanding distribution and enabling partners to issue reward
              assets like DropBoxes and Mystery Boxes.
            </li>
          </ul>
        </li>
        <li className="mb-3">
          <b>OEM and Mobile Operator Licensing:</b> Partner devices become “EarnPhones” through
          staking $EARNM, creating additional revenue streams while leveraging the Earn’M ecosystem.
        </li>
        <li>
          <p className="mb-3">
            <b>Their revenue and value generation is achieved through B2C and B2B models:</b>
          </p>
          <ImageProxy
            alt="Investors and Partners"
            src="/launchpad/stories/earnM/earnm-business-model.png"
            className="block w-full h-auto rounded-3xl object-contain"
            quality={80}
            width={773}
            height={430}
          />
        </li>
      </ul>
    ),
  },
  {
    id: 'token',
    title: "EARN'M Token",
    content: (
      <p className="mb-4">
        <p className="mb-3 font-normal max-sm:text-sm">
          The <b>$EARNM</b> token powers Earn’M’s economic structure:
        </p>
        <List items={tokenUtility} />
        <div className="flex mt-4 justify-center gap-4">
          <Button
            variant="secondary"
            className="block mt-6 max-sm:mt-4 !py-2 px-6 max-sm:w-full rounded-lg"
          >
            <ExternalLink
              className="underline"
              href="https://docs.google.com/spreadsheets/d/117gHdrlLTSgYqM2zIY1SrDyHf3tTRfHGSLosiYD3V48/edit?gid=242255297#gid=242255297"
            >
              Learn More
            </ExternalLink>
          </Button>
          <Button
            variant="outline"
            className="!border-secondary-600 !text-secondary block mt-6 max-sm:mt-4 !py-2 px-6 max-sm:w-full rounded-lg border-pink-700"
          >
            <ExternalLink className="underline" href="https://www.earnm.com/tokenomics">
              Tokenomics
            </ExternalLink>
          </Button>
        </div>
      </p>
    ),
  },
  {
    id: 'team',
    title: 'Team',
    content: (
      <p className="mb-3 font-normal max-sm:text-sm">
        <p className="mb-4">
          Earn’M is the brainchild of <b>Mode Mobile</b>, a team with over a decade of expertise in
          mobile applications, crypto, and revenue generation. Backed by a dedicated team of 60+,
          including 30 engineers, Earn’M leads in creating mobile-focused blockchain products.
        </p>
        <TeamMemberList
          className="!grid-cols-1 !items-start max-md:flex-col"
          cardClassName="max-md:flex-col"
          cardContentCentered
          teamMembers={teamMembers}
          photoIsInBlackFrame
        />
      </p>
    ),
  },
  {
    id: 'roadmap',
    title: 'Roadmap',
    content: (
      <ImageProxy
        alt="Roadmap"
        src="/launchpad/stories/earnM/earnm-roadmap.png"
        className="block w-full h-auto rounded-3xl object-contain"
        quality={80}
        width={773}
        height={629}
      />
    ),
  },
];
